/* eslint-disable max-len */
import { useMutation } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';
import AdminModal from '../../../components/AdminModal';
import TextInput from '../../../components/input';
import SelectSearch from '../../../components/SelectSearch';
import useToast from '../../../customHooks/useToast';
import { CREATE_KAABO_COUPON } from '../../../graphql/admin_mutations';
import { GET_KAABO_PARTNERS } from '../../../graphql/admin_queries';
import useCustomQuery from '../../../network/useCustomQuery';
import InfoIconNew from '../../../svgs/InfoIconNew';
import Styles from '../PartnerCoupons.module.scss';

interface ICreateCouponModal {
  closeModal: () => void;
  refetch: () => void;
}

const CreateCouponModal = ({ closeModal, refetch }: ICreateCouponModal) => {
  const Toast = useToast();
  const [couponCode, setCouponCode] = useState('');
  const [couponAlias, setcouponAlias] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState('10');
  const [maxUses, setMaxUses] = useState('1000');
  const [expiryLocal, setExpiryLocal] = useState('');
  const [success, setSuccess] = useState(false);

  const [getPartners, , partnersData] = useCustomQuery({
    query: GET_KAABO_PARTNERS,
    onError: () => {},
    onData: () => {},
  });

  useEffect(() => {
    getPartners({ variables: { offset: 0, limit: 500, search: '' } });
  }, []);

  const partnerOptions = useMemo(() => {
    const list = partnersData?.getKaaboPartners?.data?.kaaboPartners || [];
    return list.map((p: any) => ({
      label: `${p.partnerName || 'Partner'} (${p.email || p.id})`,
      value: p.id,
    }));
  }, [partnersData]);

  const [createCoupon, { loading }] = useMutation(CREATE_KAABO_COUPON);

  const handleSubmit = async () => {
    const cc = couponCode.trim();
    const ta = couponAlias.trim();
    if (cc.length < 3 && ta.length < 2) {
      Toast.error(
        'Enter a coupon code (3+ chars) or a tag / alias (2+ chars), e.g. FOUNDER26',
      );
      return;
    }
    if (!expiryLocal) {
      Toast.error('Select an expiry date and time');
      return;
    }
    const dv = Number(discountValue);
    if (Number.isNaN(dv) || dv < 0) {
      Toast.error('Enter a valid discount value');
      return;
    }
    const mu = Number(maxUses);
    if (Number.isNaN(mu) || mu < 1) {
      Toast.error('Max uses must be at least 1');
      return;
    }
    const expiryDate = new Date(expiryLocal).toISOString();
    try {
      const input: Record<string, unknown> = {
        partnerId: partnerId || null,
        expiryDate,
        maxUses: mu,
        discountType,
        discountValue: dv,
      };
      if (cc.length >= 3) input.couponCode = cc.toUpperCase();
      if (ta.length >= 2) input.couponAlias = ta;

      const res = await createCoupon({
        variables: { input },
      });
      if (res.data?.createCoupon?.status === 200) {
        setSuccess(true);
        refetch();
      } else {
        Toast.error(
          res.data?.createCoupon?.message || 'Could not create coupon',
        );
      }
    } catch (e: any) {
      Toast.error(e?.graphQLErrors?.[0]?.message || 'Something went wrong');
    }
  };

  return (
    <div className={Styles.modalFields}>
      <AdminModal
        headText={success ? 'Coupon created' : 'Create partner coupon'}
        subText={
          success ? (
            <span>The coupon is live and can be managed from the list.</span>
          ) : (
            <span>
              Set a fixed code (e.g. FOUNDER26) or a tag; if the tag is taken, a
              short suffix is added automatically.
            </span>
          )
        }
        actionBtnName={success ? 'Done' : 'Create coupon'}
        handleModalAction={success ? closeModal : handleSubmit}
        topIcon={<InfoIconNew />}
        isShow
        isSuccess={success}
        isActionBtnDisabled={loading}
        onClickAway={() => {}}
        onClose={closeModal}
        isLoading={loading}
      >
        {!success && (
          <div className={Styles.modalFields}>
            <TextInput
              name="couponCode"
              value={couponCode}
              label="Coupon code (optional if using tag)"
              customOnChange={(e: any) => setCouponCode(e.target.value)}
              backgroundColor="rgba(255, 255, 255, 1)"
              placeholder="FOUNDER26"
            />
            <p className={Styles.hint}>
              Letters and numbers only; stored in uppercase.
            </p>
            <TextInput
              name="couponAlias"
              value={couponAlias}
              label="Tag / alias (optional if code is set)"
              customOnChange={(e: any) => setcouponAlias(e.target.value)}
              backgroundColor="rgba(255, 255, 255, 1)"
              placeholder="FOUNDER"
            />
            <SelectSearch
              label=""
              options={partnerOptions}
              placeholder="Assign to partner (optional)"
              name="partner"
              value={partnerId}
              classes={false}
              hideLabel
              fullWidth
              onChange={(e: any) => setPartnerId(e?.target?.value || '')}
              disabled={false}
            />
            <SelectSearch
              label=""
              options={[
                { label: 'Percentage off', value: 'percentage' },
                { label: 'Fixed amount', value: 'fixed' },
              ]}
              placeholder="Discount type"
              name="discountType"
              value={discountType}
              classes={false}
              isRequired
              hideLabel
              fullWidth
              onChange={(e: any) => setDiscountType(e?.target?.value)}
              disabled={false}
            />
            <TextInput
              name="discountValue"
              value={discountValue}
              label={
                discountType === 'percentage' ? 'Discount %' : 'Discount amount'
              }
              customOnChange={(e: any) => setDiscountValue(e.target.value)}
              backgroundColor="rgba(255, 255, 255, 1)"
            />
            <TextInput
              name="maxUses"
              value={maxUses}
              label="Max redemptions"
              customOnChange={(e: any) => setMaxUses(e.target.value)}
              backgroundColor="rgba(255, 255, 255, 1)"
            />
            <label
              htmlFor="expiryLocal"
              style={{ fontSize: 12, display: 'block' }}
            >
              <span style={{ display: 'block', marginBottom: 6 }}>
                Expires
                <span style={{ color: '#c00' }}>*</span>
              </span>
              <input
                id="expiryLocal"
                type="datetime-local"
                value={expiryLocal}
                onChange={(e) => setExpiryLocal(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid rgba(226, 232, 240, 1)',
                  fontSize: 14,
                }}
              />
            </label>
          </div>
        )}
      </AdminModal>
    </div>
  );
};

export default CreateCouponModal;
